import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import AuthLayout, { AUTH } from './AuthLayout';

/**
 * Handles the redirect from `/api/auth/google/callback`.
 * The backend sets an httpOnly refresh cookie, then redirects here.
 * We trigger a token refresh to pick up the session, then navigate onward.
 * If `?error=OAUTH_FAILED` is present, we show an error and link back.
 */
export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuthContext();
  const error = params.get('error');

  useEffect(() => {
    if (error) return; // don't attempt refresh if the backend flagged an error

    // Wait for auth bootstrap to complete
    if (auth.loading) return;

    if (!auth.user) {
      // Refresh finished but no user — cookie may not have been set properly
      navigate('/auth/login', { replace: true });
      return;
    }

    // Wait for full profile hydration from GET /api/auth/me before checking signatureId.
    // Without this, signatureId might be undefined (not yet fetched) rather than null
    // (genuinely missing), causing false-positive signature-setup redirects.
    if (!auth.profileHydrated) return;

    // Profile is definitively hydrated — signatureId is known
    if (!auth.user.signatureId) {
      navigate('/auth/signature-setup', { replace: true });
    } else {
      localStorage.setItem('corner:onboarding-complete', '1');
      navigate('/app', { replace: true });
    }
  }, [auth.loading, auth.user, auth.profileHydrated, error, navigate]);

  if (error) {
    return (
      <AuthLayout>
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            backgroundColor: AUTH.cardBg,
            borderRadius: 16,
            padding: '40px 32px',
            boxShadow: AUTH.cardShadow,
            border: `1px solid ${AUTH.cardBorder}`,
            fontFamily: '"Geist", sans-serif',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#FDF5F3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={AUTH.textError} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: AUTH.textPrimary }}>
            {error === 'EMAIL_EXISTS' ? 'Email already registered' : 'Sign-in failed'}
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: AUTH.textSecondary, lineHeight: '20px' }}>
            {error === 'EMAIL_EXISTS'
              ? 'An account with this email already exists. Please sign in with your email and password, or try again with Google.'
              : error === 'ACCESS_DENIED'
                ? 'You cancelled the Google sign-in. No worries — try again when you\u2019re ready.'
                : 'Google sign-in didn\u2019t complete. This can happen if the popup was closed or your account isn\u2019t eligible.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(error === 'EMAIL_EXISTS' ? '/auth/login' : '/auth/login', { replace: true })}
            style={{
              marginTop: 4,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              backgroundColor: AUTH.accent,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: '"Geist", sans-serif',
            }}
          >
            {error === 'EMAIL_EXISTS' ? 'Sign in with email \u2192' : 'Try again \u2192'}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Loading state while useAuth restores the session
  return (
    <AuthLayout>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 32,
            height: 32,
            border: `3px solid var(--auth-card-border)`,
            borderTopColor: AUTH.accent,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ fontSize: 14, color: AUTH.textSecondary, fontFamily: '"Geist", sans-serif' }}>
          Signing you in…
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </AuthLayout>
  );
}
